import React, { useEffect, useState } from "react";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useContactVendorsMessageTemplate } from "../../../../../hooks/useTemplates";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { VendorEmailDisplayDialog } from "../../../../dialog/VendorEmailDisplayDialog";
import { Error as RequestsError } from "../../../../../copy/requests";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import {
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
} from "../../../../../util/vendors";
import {
  isUploadVendorForOptOutWorkflow,
  isVendorAllowedInOptOutWorkflow,
} from "../../../../../util/optOutWorkflowVendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import { Callout, CalloutVariant } from "../../../../Callout";
import { Link } from "react-router-dom";
import { Routes } from "../../../../../root/routes";
import { EmailButton } from "../../../../organisms/EmailButton";
import { useApiTrigger } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { ContactVendorRequest } from "../../../../../../common/service/server/dto/UpdateVendorRequest";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { Tooltip } from "@mui/material";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { Organization } from "../../../../../../common/models";
import { OrganizationManagedAdNetworkDto } from "../../../../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { getOptOutWorkflowVendorSelection } from "../../../../../util/optOutWorkflowVendors";
import { getVendorNesting, isConnectedSystemVendor } from "../../../../../util/vendorNesting";

const getCompletedVendors = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
) => {
  return (
    vendors?.filter(
      (v) =>
        (v.email || v.defaultEmail) &&
        request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId && x.processed),
    ) || []
  );
};

const isSuccessfulAutomatedOutcome = (vendorOutcome?: string): boolean => {
  return ["AUTOMATION_REQUESTED", "AUTOMATION_NO_DATA", "AUTOMATION_CONFIRMED"].includes(
    vendorOutcome || "",
  );
};

/**
 * If `request` is null, then will only use request-agnostic filters (so the result may over-include vendors that would be filtered out in the context of a specific request)
 * If no specific `request` is provided, then `dataMap` and `organization` params are ignored
 */
export const filterVendorsForOptOutNotifyThirdPartiesStep = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto | null,
  dataMap: DataMapDto | null,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  organization: Organization | null,
) => {
  const { managerVendorIds, managedVendorIds, directMailVendorIds } =
    getOptOutWorkflowVendorSelection(vendors, sellingAndSharingAnswers, managedAdNetworks);

  return (vendors || []).filter((v) => {
    if (!isVendorAllowedInOptOutWorkflow(v)) {
      return false;
    }

    // TrueVault talks to the Connected System through its automation, so it is never emailed like a third-party
    // recipient - including as a manager standing in for the recipients it handles (POL-7958).
    if (isConnectedSystemVendor(v)) {
      return false;
    }

    const optOutInstruction = v.instructions.find((vi) => vi.requestType == "OPT_OUT");
    const isContactVendor =
      (isUploadVendorForOptOutWorkflow(v) ||
        v.usesCustomAudience ||
        managerVendorIds.includes(v.id) ||
        directMailVendorIds.includes(v.id)) &&
      !managedVendorIds.includes(v.id) &&
      optOutInstruction?.doNotContact !== true;

    // Main request-agnostic check
    if (!isContactVendor) {
      return false;
    }

    // If no specific request provided, then can only use result of request-agnostic check
    if (!request) {
      return isContactVendor;
    }

    // Both org and data map are required for request-specific checks
    if (!organization || !dataMap) {
      return false;
    }

    const vendorOutcome = request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId);
    return (
      isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context,
        v,
        request,
        organization,
      ) &&
      v.processingRegions?.includes(getRequestProcessingRegion(request)) &&
      !isSuccessfulAutomatedOutcome(vendorOutcome?.outcome)
    );
  });
};

export const filterVendorsToContactForOptOutRequest = (
  optOutVendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
) => {
  return optOutVendors.filter(
    (v) =>
      (v.email || v.defaultEmail) &&
      (request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId && !x.processed) ||
        request.vendorOutcomes.every((x) => x.vendor.id !== v.vendorId)),
  );
};

export const NotifyThirdParties = () => {
  const [org] = usePrimaryOrganization();
  const [allVendors] = useOrganizationVendors(org.id, undefined, true);
  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);

  const { data: dataMap } = useDataMap(org.id);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const [updateVendorOutcome, updateVendorOutcomeRequest] = useApiTrigger(
    (values: ContactVendorRequest) => Api.dataRequest.contactVendors(org.id, request.id, values),
    refreshRequest,
    RequestsError.TransitionProcessing,
  );

  const optOutVendors = filterVendorsForOptOutNotifyThirdPartiesStep(
    allVendors,
    request,
    dataMap,
    answers,
    managedAdNetworks,
    org,
  );
  const completedVendors = getCompletedVendors(optOutVendors, request);
  const vendorsToContact = filterVendorsToContactForOptOutRequest(optOutVendors, request);

  // Nested vendors are handled by their manager, so a missing contact email on one is not actionable here.
  const { isManaged } = getVendorNesting(allVendors, managedAdNetworks, answers);

  const vendorsWithoutEmail = optOutVendors.filter(
    (v) => !v.email && !v.defaultEmail && !isManaged(v.id),
  );
  const actionableVendorIds = new Set([
    ...completedVendors.map((v) => v.vendorId),
    ...vendorsToContact.map((v) => v.vendorId),
    ...vendorsWithoutEmail.map((v) => v.vendorId),
  ]);
  const visibleOptOutVendors = optOutVendors.filter((v) => actionableVendorIds.has(v.vendorId));
  const vendorsContacted = completedVendors.length > 0;

  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [removedRecipients, setRemovedRecipients] = useState(new Set<string>());

  const [template] = useContactVendorsMessageTemplate(org.id, request.id);

  const hasValidRecipients = vendorsToContact.some((v) => !removedRecipients.has(v.vendorId));

  useEffect(() => {
    setStepCompleted(!hasValidRecipients || (vendorsContacted && vendorsWithoutEmail.length === 0));
  }, [vendorsContacted, vendorsWithoutEmail.length, hasValidRecipients]);

  const dispatchEmail = () => {
    const values = {
      vendors: vendorsToContact.map((v) => v.vendorId).filter((id) => !removedRecipients.has(id)),
    };
    updateVendorOutcome(values);
  };

  return (
    <>
      {visibleOptOutVendors.length === 0 ? (
        <p>No vendors need to be contacted for this request. You can proceed to the next step.</p>
      ) : (
        <p>
          To fulfill your data privacy obligation, the message below will be emailed to any vendors
          to inform them of the opt-out request.
        </p>
      )}

      {vendorsContacted && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            <p>
              Message successfully sent to{" "}
              <strong>{completedVendors.map((v) => v.name).join(", ")}.</strong>
            </p>
          </Callout>
        </div>
      )}

      {vendorsToContact.length > 0 && (
        <>
          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To:</strong>{" "}
                {vendorsContacted ? (
                  <Tooltip title="You have already contacted vendors for this request">
                    <span className="color-neutral-600 text-decoration-underline">
                      See Recipients
                    </span>
                  </Tooltip>
                ) : (
                  <Link to="#" onClick={() => setRecipientsModalOpen(true)}>
                    See Recipients
                  </Link>
                )}
              </p>
              <p>
                <strong>
                  Subject: {request.dataRequestType.regulationLaw.name}{" "}
                  {request.dataRequestType.label}
                </strong>
              </p>

              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>
        </>
      )}

      {vendorsWithoutEmail.length > 0 &&
        (vendorsWithoutEmail.length != optOutVendors.length ? (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent to the following Data Recipients because there is no
                email address added for them:{" "}
                <strong>{vendorsWithoutEmail.map((v) => v.name).join(", ")}.</strong>
              </p>
              <p>
                {" "}
                Visit{" "}
                <a href={Routes.organization.requestHandling.requestToOptOut}>
                  Manage Request to Opt-Out Settings
                </a>{" "}
                to add a contact email.
              </p>
            </Callout>
          </div>
        ) : (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent because none of your Data Recipients can be
                contacted. Add email addresses for the following Data Recipients in{" "}
                <a href={Routes.organization.requestHandling.requestToOptOut}>
                  Manage Request to Opt-Out Settings
                </a>
                , then return to this step to preview and send the notification:{" "}
                <strong>{vendorsWithoutEmail.map((v) => v.name).join(", ")}.</strong>
              </p>
            </Callout>
          </div>
        ))}

      {(vendorsToContact.length > 0 || vendorsWithoutEmail.length > 0) && (
        <EmailButton
          color="primary"
          variant="contained"
          className="btn-md mb-std"
          loading={updateVendorOutcomeRequest.running}
          onClick={dispatchEmail}
          disabled={vendorsWithoutEmail?.length > 0 || !hasValidRecipients || vendorsContacted}
          disabledTooltip={
            vendorsContacted
              ? "You have already contacted vendors for this request"
              : hasValidRecipients
              ? vendorsWithoutEmail?.length > 0
                ? "There are data recipients with no email address associated"
                : undefined
              : "You must contact at least one vendor"
          }
        >
          Send
        </EmailButton>
      )}

      <VendorEmailDisplayDialog
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        vendors={vendorsToContact}
        allowToggle={true}
        isRecipientRemoved={(v) => removedRecipients.has(v.vendorId)}
        removeRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients, v.vendorId]));
        }}
        addRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients].filter((id) => id !== v.vendorId)));
        }}
      />
    </>
  );
};
